import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import styled, { useTheme } from "styled-components/macro";
import useSWR from "swr";
import { endpoints } from "../../endpoints";
import { Form, FormFieldAndIcon } from "../../layout/FormLayout";
import { AddOrEditImageAttribute } from "../../pages/admin/SellerAdmin/PIM/SellerAdminPIMAttributes/ImageAttributes/AddOrEditImageAttribute";
import { stringArrayToOptionArray } from "../../pages/admin/SellerAdmin/PIM/SellerAdminTemplates/AddItemToTemplateCollectionForm";
import type {
  CreatedOption,
  MethodsOfUseForm,
  OptionType,
  User,
  UUID,
} from "../../types/types";
import { isCreatedOption } from "../../types/types";
import type {
  Assets,
  AttributeSchema,
  ImageAttributeValue,
  PIMProduct,
} from "../../types/types.PIM";
import { getEditBlockedReasonMessage } from "../../util/attributeEditability";
import {
  attributeNeedsChoices,
  buildListCreatableOptionProps,
  useLazyListBackedChoices,
} from "../../util/attributeChoices";
import { convertUserToOption, useStoreState } from "../../util/util";
import { LinkAttributeValueSchema } from "../../util/zod.util";
import { useAuthContext } from "../Auth";
import {
  ButtonWithConfirmDialog,
  DestructiveButtonLargeV2,
  PrimaryButtonLargeV2,
  SecondaryButtonMediumV2,
  XSmallButtonRow,
} from "../Buttons/Buttons";
import type { ChipType } from "../Chips/Chips";
import { CreatableSelectBox } from "../CreateableSelectBox/CreatableSelectBox";
import { DatePicker } from "../DatePicker/DatePicker";
import { DeletableChips } from "../DeletableChips/DeletableChips";
import { ErrorPlaceholder } from "../Error";
import {
  InfoIcon,
  LoadingIcon,
  NonVisibleIcon,
  NotApplicableIcon,
} from "../Icons/Icons";
import { RichEditor } from "../RichEditor/RichEditor";
import { SearchSelect } from "../SearchSelect/SearchSelect";
import { SearchSelectInfiniteScroll } from "../SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { SlideOut } from "../SlideOut/SlideOut";
import { TextArea } from "../TextArea/TextArea";
import { TextField } from "../TextFields/TextFields";
import { ToggleSwitchV2 } from "../ToggleSwitch/ToggleSwitch";
import { SoftHeaderRegular } from "../Typography/Typography";
import { ImageAttributePreview } from "./ImageAttributePreview";
import { PlusIcon } from "../Icons/Icons";
import { ObjectReferenceField } from "../ObjectReference/ObjectReferenceField";
import type { ProductGroupFormValues } from "../../pages/SharedPages/ProductDetailPage/ProductOverview/product_overview.utils";
import { MultiSelectRefType } from "../../pages/admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/type";
import { useDependentAttributes } from "../../util/hooks";
import { AddressAttributeForm } from "../../pages/admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/AddressAttributeForm";
import {
  AddressValue,
  EMPTY_ADDRESS,
  prepareAddressForForm,
} from "../../pages/admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/address.utils";
import {
  formatMandatoryLabel,
  formatRequiredLabel,
} from "../../pages/SharedPages/OrganizationPage/ProductsList/NewProductForm/utils";
import { sortOptionsByLabel } from "../../util/options";
import { LinkAttributeField } from "../LinkAttributeField/LinkAttributeField";
import { spacingTokens } from "../../designTokens";
import { DocumentAttribute } from "./DocumentAttribute/DocumentAttribute";
import type { DocumentAttributeValue } from "../../types/types.PIM";
import { SingleSelectListBackedField } from "./ListBackedSelectField";

export { DocumentAttribute } from "./DocumentAttribute/DocumentAttribute";
export type { DocumentAttributeProps } from "./DocumentAttribute/DocumentAttribute";

export const InputWrapper = styled.div`
  width: 100%;
  max-width: 520px;
  margin-bottom: 10px;
`;

const CenteredIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: start;
  padding-top: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const FlexForm = styled(Form)`
  display: flex;
  flex-direction: column;
  div[class*="auto-filled-indicator"] {
    > svg {
      width: 20px;
      height: 20px;
    }
  }
`;

export const LinkTextFieldsContainer = styled.div`
  max-width: 500px;
  display: grid;
  grid-template-columns: 48% auto 48%;
  flex-wrap: nowrap;
  overflow: hidden;
  div.text_errorMessageWrapper {
    position: unset;
  }
`;

export const GreyLineWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const GreyLine = styled.div`
  height: 1px;
  width: 100%;
  border: solid ${({ theme }) => theme.resolve("border_md")}
    ${({ theme }) => theme.primaryBorder};
`;

const LoadingIconContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
`;

const RichEditorLocalContainer = styled.div`
  .ql-container {
    height: fit-content;
  }
`;

export type ImageAttributeProps = {
  attribute: AttributeSchema & { attribute_id?: string };
  methodsOfUseForm: MethodsOfUseForm;
  onCreateImageAttribute?: (
    image_arr: ImageAttributeValue | null,
    attribute: AttributeSchema & { attribute_id?: string }
  ) => void;
  isDisabled?: boolean;
  isNotApplicable?: boolean;
};

export const ImageAttribute = ({
  attribute,
  methodsOfUseForm,
  onCreateImageAttribute,
  isDisabled,
  isNotApplicable,
}: ImageAttributeProps) => {
  const [showSlideout, setShowSlideout] = useState(false);
  const [imageAttribute, setImageAttribute] =
    useState<ImageAttributeValue | undefined>();
  const [isDeleted, setIsDeleted] = useState(false);
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const theme = useTheme();

  const attributeId = useMemo(
    () => (attribute.attribute_id ? attribute.attribute_id : attribute.id),
    [attribute.attribute_id, attribute.id]
  );

  const hasValidationError = useMemo(
    () => methodsOfUseForm.errors[attributeId],
    [attributeId, methodsOfUseForm.errors]
  );

  const assetId = useMemo(() => {
    if (imageAttribute?.asset_id) {
      return imageAttribute.asset_id;
    }
    if (attribute.values?.length && !isDeleted) {
      return (attribute.values[0].value as ImageAttributeValue).asset_id;
    }
    return null;
  }, [imageAttribute?.asset_id, attribute.values, isDeleted]);

  const { data: asset, error: assetError } = useSWR<Assets>(
    assetId ? endpoints.v2_tenants_id_pim_assets_id(tenant_id, assetId) : null,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000, // 1 minute
    }
  );

  const closeSlideout = useCallback(() => {
    setShowSlideout(false);
  }, []);

  const onComplete = useCallback(
    (imageAttr: ImageAttributeValue | null, canClose = true) => {
      setImageAttribute(imageAttr ?? undefined);
      setIsDeleted(!imageAttr);
      onCreateImageAttribute?.(imageAttr, attribute);
      if (canClose) {
        closeSlideout();
      }
    },
    [onCreateImageAttribute, attribute, closeSlideout]
  );

  useEffect(() => {
    methodsOfUseForm.register({
      name: attributeId,
      required: attribute.is_required,
    });

    if (!imageAttribute && !isDeleted) {
      const defaultValue = attribute.values?.length
        ? (attribute.values[0].value as ImageAttributeValue)
        : undefined;
      methodsOfUseForm.setValue(attributeId, defaultValue);
    }
  }, [
    attribute.is_required,
    attribute.values,
    attributeId,
    imageAttribute,
    isDeleted,
    methodsOfUseForm,
  ]);

  const isLoadingAsset = !assetError && !asset && assetId;

  if (isLoadingAsset) {
    return (
      <LoadingIconContainer>
        <LoadingIcon width={24} height={24} />
      </LoadingIconContainer>
    );
  }

  if (assetError) {
    return <ErrorPlaceholder message={t("Could not load image")} />;
  }

  if (assetId && asset && !isNotApplicable) {
    return (
      <ImageAttributePreview
        attribute={attribute}
        asset={asset}
        onCreateImageAttribute={(imageAttr) => {
          onComplete(imageAttr, false);
        }}
        image_attribute_value={imageAttribute}
      />
    );
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "fit-content",
          gap: spacingTokens.spacing_md,
        }}
      >
        <SoftHeaderRegular
          style={{
            color: hasValidationError
              ? theme.resolve("color_text_error")
              : theme.resolve("color_text_primary"),
          }}
        >
          {formatRequiredLabel(attribute, t)}
        </SoftHeaderRegular>
        <SecondaryButtonMediumV2
          type="button"
          onClick={() => setShowSlideout(true)}
          disabled={isDisabled || isNotApplicable}
        >
          <PlusIcon />
          {t("Add Image")}
        </SecondaryButtonMediumV2>
      </div>
      <SlideOut
        title={t("Add Image")}
        show={showSlideout}
        closeFlyout={closeSlideout}
      >
        <AddOrEditImageAttribute
          attribute={attribute}
          onCancel={closeSlideout}
          onComplete={onComplete}
          existing_asset={!isDeleted ? asset : undefined}
        />
      </SlideOut>
    </>
  );
};

export const FormFieldAndIcons = ({
  description,
  restricted,
  children,
  attribute,
  applicable,
  disable_applicable_button,
  onClickNotApplicable,
  pimProductType,
  showApplicableButton = true,
  isDisabled = false,
}: {
  description?: string | null;
  restricted?: boolean;
  children: ReactNode;
  attribute: AttributeSchema;
  applicable?: boolean;
  disable_applicable_button?: boolean;
  onClickNotApplicable: () => void;
  pimProductType: PIMProduct["pim_product_type"];
  showApplicableButton?: boolean;
  isDisabled?: boolean;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { hasPermission } = useAuthContext();

  const is_master_variant_attribute_disabled_tooltip =
    attribute.object_type === "product_name"
      ? ""
      : attribute.is_master && pimProductType === "variant"
      ? t(
          "This is a master attribute, and is editable only on a master product."
        )
      : !attribute.is_master && pimProductType === "master"
      ? t(
          "This is a variant attribute, and is editable only on a variant product"
        )
      : "";

  const disabledTooltip = isDisabled
    ? getEditBlockedReasonMessage(attribute.edit_blocked_reason, t) ||
      is_master_variant_attribute_disabled_tooltip
    : "";

  return (
    <FormFieldAndIcon>
      <div data-tip={disabledTooltip} data-for="master-variant-tip">
        {children}
      </div>
      {description && (
        <>
          <CenteredIconContainer data-tip={description} data-for="info-tip">
            <InfoIcon fill={theme.secondaryIconColor} />
          </CenteredIconContainer>
          <ReactTooltip
            delayHide={500}
            id="info-tip"
            clickable
            effect="solid"
          />
        </>
      )}
      {restricted && (
        <>
          <CenteredIconContainer
            data-for="restricted-tip"
            data-tip={t("This attribute is only visible for internal users")}
          >
            <NonVisibleIcon
              width={24}
              height={24}
              fill={theme.secondaryIconColor}
            />
          </CenteredIconContainer>
          <ReactTooltip delayHide={500} id="restricted-tip" effect="solid" />
        </>
      )}
      {showApplicableButton &&
        attribute.is_editable &&
        !attribute.is_mandatory &&
        !attribute.is_required && (
          <>
            <ButtonWithConfirmDialog
              Button={({ onClick }) => (
                <CenteredIconContainer
                  as="button"
                  type="button"
                  disabled={
                    !hasPermission("modify_products") ||
                    disable_applicable_button
                  }
                  onClick={onClick}
                  css={`
                    background: none;
                    border: none;
                    margin-top: 12px;
                    width: 24px;
                    height: 24px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;
                    cursor: ${({ disabled }: { disabled?: boolean }) =>
                      disabled ? "not-allowed" : "pointer"};
                  `}
                >
                  <NotApplicableIcon
                    width={24}
                    height={24}
                    fill={
                      !applicable ? theme.errorColor : theme.secondaryIconColor
                    }
                  />
                </CenteredIconContainer>
              )}
              testid="not-applicable-button"
              confirmMessage={t(
                applicable
                  ? "Are you sure you want to mark this field as not applicable?"
                  : "Are you sure you want to mark this field as applicable?"
              )}
              handleConfirm={() => onClickNotApplicable()}
              datatip={
                hasPermission("modify_products")
                  ? t("Mark as not applicable")
                  : t(
                      "You don't have permission to mark attributes as not applicable"
                    )
              }
              datafor="not-applicable-tip"
            />
            <ReactTooltip
              delayHide={500}
              id="not-applicable-tip"
              effect="solid"
            />
          </>
        )}
      <ReactTooltip id="master-variant-tip" effect="solid" />
    </FormFieldAndIcon>
  );
};

export type AttributeGroupFormGeneratorProps = {
  attributes: AttributeSchema[];
  methodsOfUseForm: MethodsOfUseForm;
  onSubmit: (values: ProductGroupFormValues) => Promise<JSX.Element | void>;
  onCancel: () => void;
  isLoading: boolean;
  onCreateOption: (value: string, attribute: AttributeSchema) => Promise<void>;
  onCreateImageAttribute?: (
    image_arr: ImageAttributeValue | null,
    attribute: AttributeSchema
  ) => void;
  onCreateDocumentAttribute?: (
    docs: DocumentAttributeValue[],
    attribute: AttributeSchema & { attribute_id?: string }
  ) => void;
  product?: PIMProduct;
  showMandatoryAsterisk?: boolean;
  pimProductType?: PIMProduct["pim_product_type"];
  updateApplicability: (
    attributesToMakeApplicable: UUID[],
    attributesToMakeUnapplicable: UUID[]
  ) => Promise<void>;
  has_reference_entity_mapping?: boolean;
  group_id?: string;
  showApplicableButton?: boolean;
  objectSearchExcludeProfileId?: string;
};

// Helper function to format label with asterisk if mandatory

/**
 * Takes in columns and outputs a form. Supports all values of @see AttributeInput
 */
export function AttributeGroupFormGenerator({
  attributes,
  methodsOfUseForm,
  onSubmit,
  onCancel,
  onCreateOption,
  onCreateImageAttribute,
  onCreateDocumentAttribute,
  product,
  isLoading,
  showMandatoryAsterisk,
  pimProductType = "standard",
  updateApplicability,
  has_reference_entity_mapping = false,
  group_id,
  showApplicableButton = true,
  objectSearchExcludeProfileId,
}: AttributeGroupFormGeneratorProps): JSX.Element {
  const {
    register,
    formState,
    errors,
    control,
    setValue,
    handleSubmit,
    watch,
    getValues,
  } = methodsOfUseForm;
  const {
    tenant_id,
    storefront_id,
    storefront_metadata: { enable_reference_entities },
  } = useStoreState();
  const [attribute_to_update, set_attribute_to_update] = useState<string>();

  const [disable_applicable_button, set_disable_applicable_button] =
    useState(false);
  const { t } = useTranslation();
  const { hasPermission } = useAuthContext();
  const editConstraintsFetched = useRef(false);

  const multiSelectRefs = useRef<{
    [key: string]: MultiSelectRefType;
  }>({});

  const {
    clearableAttributes,
    dependentAttributesOptions: dependent_attributes_options,
    updateDependentAttributes,
  } = useDependentAttributes({
    mode: "single",
    storefrontId: storefront_id,
    enabled: !!has_reference_entity_mapping && !!group_id,
    contextType: "group",
    contextId: group_id ?? "",
    getFormValues: getValues,
    setFormValue: setValue,
    attributesOrColumns: attributes,
    multiSelectRefs,
    multiSelectValueFormat: "string",
  });

  const datePickerRef = useRef<{ updateDate: (date: string) => void }>(null);

  const isClearable = useCallback(
    (col_id: string) => {
      return has_reference_entity_mapping
        ? clearableAttributes?.has(col_id) ||
            (clearableAttributes || new Set()).size === 0
        : true;
    },
    [clearableAttributes, has_reference_entity_mapping]
  );

  const isAutofilled = useCallback(
    (col_id: string) => {
      const constraint = dependent_attributes_options?.find(
        (c) => c.attribute_id === col_id
      );
      return constraint?.action === "auto_fill";
    },
    [dependent_attributes_options]
  );

  const onClickNotApplicable = async (attr: AttributeSchema) => {
    const attributesToMakeApplicable: UUID[] = [];
    const attributesToMakeUnapplicable: UUID[] = [];
    attributes.forEach(({ is_not_applicable, id }) => {
      if (id === attr.id) {
        // Toggle applicability for the clicked attribute
        (is_not_applicable
          ? attributesToMakeApplicable
          : attributesToMakeUnapplicable
        ).push(id);
      } else {
        // Keep existing state for other attributes
        (is_not_applicable
          ? attributesToMakeUnapplicable
          : attributesToMakeApplicable
        ).push(id);
      }
    });
    set_disable_applicable_button(true);
    await updateApplicability(
      attributesToMakeApplicable,
      attributesToMakeUnapplicable
    );
    const newNotApplicableState = !attr.is_not_applicable;
    if (newNotApplicableState) {
      switch (attr?.input_type) {
        case "date":
          methodsOfUseForm.setValue(attr.id, "", {
            shouldValidate: true,
            shouldDirty: true,
          });
          break;
        case "form_field":
          methodsOfUseForm.setValue(attr.id, "", {
            shouldValidate: true,
            shouldDirty: true,
          });
          break;
        case "multiline_entry":
          methodsOfUseForm.setValue(attr.id, "", {
            shouldValidate: true,
            shouldDirty: true,
          });

          break;
        case "multi_select":
          methodsOfUseForm.setValue(attr.id, []);
          multiSelectRefs.current[attr.id]?.clearChips();
          break;
        case "link":
          setTimeout(() => {
            setValue(
              attr.id,
              { display_text: "", url: "" },
              {
                shouldValidate: true,
                shouldDirty: true,
              }
            );
          }, 0);

          break;
        case "product_reference":
        case "user":
        case "object_reference":
          methodsOfUseForm.setValue(
            attr.id,
            attr.is_multiple_choice ? [] : "",
            {
              shouldValidate: true,
              shouldDirty: true,
            }
          );
          break;

        case "address":
          Object.keys(EMPTY_ADDRESS).forEach((field) => {
            methodsOfUseForm.setValue(`${attr.id}.${field}`, "", {
              shouldValidate: true,
              shouldDirty: true,
            });
          });
          methodsOfUseForm.setValue(`${attr.id}.country`, null, {
            shouldValidate: true,
            shouldDirty: true,
          });
          methodsOfUseForm.setValue(`${attr.id}.state`, null, {
            shouldValidate: true,
            shouldDirty: true,
          });
          methodsOfUseForm.setValue(`${attr.id}.poc_country_code`, null, {
            shouldValidate: true,
            shouldDirty: true,
          });
          break;
        default:
          methodsOfUseForm.setValue(attr.id, "");
      }
      set_disable_applicable_button(false);
    } else {
      set_attribute_to_update(attr.id);
    }
  };

  const update_attribute = useCallback(
    (attr_id: string, attributes: AttributeSchema[]) => {
      const attr = attributes.find((a) => a.id === attr_id);
      if (attr) {
        switch (attr.input_type) {
          case "link": {
            const originalValue = attr.values?.[0]?.value;
            const parsedLink =
              LinkAttributeValueSchema.safeParse(originalValue);
            setValue(
              attr.id,
              parsedLink.success
                ? parsedLink.data
                : { display_text: "", url: "" },
              { shouldValidate: true, shouldDirty: true }
            );
            break;
          }
          case "date": {
            if (attr.values?.length) {
              const dateValue = attr.values[0].value as string;
              setValue(attr.id, dateValue, {
                shouldValidate: true,
                shouldDirty: true,
              });
              datePickerRef.current?.updateDate(dateValue);
            }
            break;
          }
          case "single_select": {
            if (attr.values?.length) {
              const variant = attr.values[0].enum_variant?.variant;
              setValue(
                attr.id,
                { label: variant, value: variant },
                { shouldValidate: true, shouldDirty: true }
              );
            }
            break;
          }
          case "toggle":
          case "checkbox": {
            if (attr.values?.length) {
              setValue(attr.id, attr.values[0].value as boolean, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
            break;
          }
          case "multi_select": {
            if (attr.values?.length) {
              const values = attr.values.map(
                (v) => v.enum_variant?.variant ?? v.attribute_id
              );
              setValue(attr.id, values, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
            break;
          }
          case "multiline_entry": {
            if (attr.values?.length) {
              setValue(attr.id, attr.values[0].value as string, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
            break;
          }
          case "product_reference": {
            if (!attr.values?.length) break;

            if (attr.is_multiple_choice) {
              const values = attr.values.map((val) => ({
                label: val.product_ref?.name ?? val.attribute_id,
                value: val.product_ref?.id,
              }));
              setValue(attr.id, values, {
                shouldValidate: true,
                shouldDirty: true,
              });
            } else if (attr.values[0].product_ref) {
              setValue(
                attr.id,
                {
                  label: attr.values[0].product_ref.name,
                  value: attr.values[0].product_ref.id,
                },
                { shouldValidate: true, shouldDirty: true }
              );
            }
            break;
          }
          case "user": {
            if (!attr.values?.length) break;

            if (attr.is_multiple_choice) {
              const values = attr.values.map((val) =>
                convertUserToOption(val.user as User, false)
              );
              setValue(attr.id, values, {
                shouldValidate: true,
                shouldDirty: true,
              });
            } else if (attr.values[0].user) {
              setValue(
                attr.id,
                convertUserToOption(attr.values[0].user as User, false),
                { shouldValidate: true, shouldDirty: true }
              );
            }
            break;
          }
          case "object_reference": {
            if (!attr.values?.length) break;

            if (attr.is_multiple_choice) {
              const values = attr.values
                .filter(
                  (val) =>
                    val.object_ref?.id ||
                    (typeof val.value === "string" && val.value.length > 0)
                )
                .map((val) => ({
                  label:
                    val.object_ref?.name ??
                    (typeof val.value === "string" ? val.value : ""),
                  value:
                    val.object_ref?.id ??
                    (typeof val.value === "string" ? val.value : ""),
                }));
              setValue(attr.id, values, {
                shouldValidate: true,
                shouldDirty: true,
              });
            } else if (
              attr.values[0].object_ref ||
              (typeof attr.values[0].value === "string" &&
                attr.values[0].value.length > 0)
            ) {
              setValue(
                attr.id,
                {
                  label:
                    attr.values[0].object_ref?.name ??
                    (typeof attr.values[0].value === "string"
                      ? attr.values[0].value
                      : ""),
                  value:
                    attr.values[0].object_ref?.id ??
                    (typeof attr.values[0].value === "string"
                      ? attr.values[0].value
                      : ""),
                },
                { shouldValidate: true, shouldDirty: true }
              );
            }
            break;
          }
          case "address": {
            if (attr.values?.length) {
              const prepared = prepareAddressForForm(
                attr.values[0].value as AddressValue
              );
              Object.entries(prepared).forEach(([field, val]) => {
                if (val !== undefined) {
                  setValue(`${attr.id}.${field}`, val, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }
              });
            }
            break;
          }
        }
      }
    },
    [setValue]
  );

  useEffect(() => {
    if (attribute_to_update) {
      update_attribute(attribute_to_update, attributes);
      set_attribute_to_update(undefined);
      set_disable_applicable_button(false);
    }
  }, [attribute_to_update, attributes, update_attribute]);

  useEffect(() => {
    if (
      getValues() &&
      enable_reference_entities &&
      has_reference_entity_mapping &&
      group_id &&
      !editConstraintsFetched.current
    ) {
      editConstraintsFetched.current = true;
      updateDependentAttributes({
        clonedSelectionsValues: getValues() as Record<string, unknown>,
      });
    }
  }, [
    enable_reference_entities,
    getValues,
    group_id,
    has_reference_entity_mapping,
    updateDependentAttributes,
  ]);

  return (
    <FlexForm onSubmit={handleSubmit(onSubmit)}>
      {attributes.map((attr) => {
        const isDisabled =
          !attr.is_editable ||
          attr.is_not_applicable ||
          (attr.object_type !== "product_name" &&
            ((attr.is_master && pimProductType === "variant") ||
              (!attr.is_master && pimProductType === "master")));

        switch (attr.input_type) {
          case "date":
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                isDisabled={isDisabled}
                showApplicableButton={showApplicableButton}
                disable_applicable_button={disable_applicable_button}
              >
                <DatePicker
                  isOutsideRange={() => false}
                  label={formatMandatoryLabel(attr, t, showMandatoryAsterisk)}
                  name={attr.id}
                  defaultValue={
                    attr.values?.length
                      ? (attr.values[0].value as string)
                      : undefined
                  }
                  required={attr.is_required}
                  methodsOfUseForm={methodsOfUseForm}
                  disabled={isDisabled}
                  ref={datePickerRef}
                  appendToBody={true}
                />
              </FormFieldAndIcons>
            );
          case "form_field": {
            const fieldKey = `${attr.id}-${
              attr.is_not_applicable ? "na" : "norm"
            }`;

            return (
              <FormFieldAndIcons
                key={attr.id}
                applicable={!attr.is_not_applicable}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                isDisabled={isDisabled}
                showApplicableButton={showApplicableButton}
                disable_applicable_button={disable_applicable_button}
              >
                <TextField
                  key={fieldKey}
                  name={attr.id}
                  label={formatMandatoryLabel(attr, t, showMandatoryAsterisk)}
                  theref={register({
                    required: attr.is_required,
                  })}
                  defaultValue={
                    attr.values?.length
                      ? (attr.values[0].value as string)
                      : undefined
                  }
                  formState={formState}
                  errors={errors}
                  type="text"
                  readOnly={attr.object_type === "product_id" || isDisabled}
                />
              </FormFieldAndIcons>
            );
          }
          case "single_select": {
            const dependent_choices = sortOptionsByLabel(
              dependent_attributes_options
                .find((constraint) => constraint.attribute_id === attr.id)
                ?.valid_values?.map((value) => ({
                  label: value.display,
                  value: value.display,
                })) ?? []
            );
            const usesDependentChoices =
              enable_reference_entities &&
              has_reference_entity_mapping &&
              dependent_attributes_options.some(
                (constraint) => constraint.attribute_id === attr.id
              );

            if (!usesDependentChoices && attributeNeedsChoices(attr)) {
              return (
                <FormFieldAndIcons
                  key={attr.id}
                  description={attr.description ? t([attr.description]) : null}
                  restricted={attr.is_restricted}
                  applicable={!attr.is_not_applicable}
                  onClickNotApplicable={() => onClickNotApplicable(attr)}
                  attribute={attr}
                  pimProductType={pimProductType}
                  isDisabled={isDisabled}
                  showApplicableButton={showApplicableButton}
                  disable_applicable_button={disable_applicable_button}
                >
                  <SingleSelectListBackedField
                    tenantId={tenant_id}
                    attribute={attr}
                    control={control}
                    errors={errors}
                    formState={formState}
                    isDisabled={isDisabled}
                    isClearable={isClearable(attr.id)}
                    isAutofilled={isAutofilled(attr.id)}
                    showMandatoryAsterisk={showMandatoryAsterisk}
                    canCreateOptions={hasPermission("modify_lists")}
                    onCreateOption={onCreateOption}
                  />
                </FormFieldAndIcons>
              );
            }

            const choices = attr?.choices?.map(stringArrayToOptionArray) ?? [];
            const creatableProps = buildListCreatableOptionProps({
              t,
              listName: attr.list_name,
              optionValues: choices.map(({ value }) => value),
              onCreateOption: (value) => onCreateOption(value, attr),
              enabled: hasPermission("modify_lists"),
            });

            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                isDisabled={isDisabled}
                showApplicableButton={showApplicableButton}
                disable_applicable_button={disable_applicable_button}
              >
                <Controller
                  control={control}
                  name={attr.id}
                  defaultValue={
                    usesDependentChoices
                      ? {
                          label: dependent_choices?.find(
                            (choice) =>
                              choice.value ===
                              attr?.values?.[0]?.enum_variant?.variant
                          )?.label,
                          value: dependent_choices?.find(
                            (choice) =>
                              choice.value ===
                              attr?.values?.[0]?.enum_variant?.variant
                          )?.value,
                        }
                      : attr?.values?.length
                      ? {
                          label: attr.values[0].enum_variant?.variant,
                          value: attr.values[0].enum_variant?.variant,
                        }
                      : null
                  }
                  rules={{
                    required: attr.is_required,
                  }}
                  render={({ onChange, ...rest }) => (
                    <CreatableSelectBox
                      {...rest}
                      isClearable={isClearable(attr.id)}
                      isAutofilled={isAutofilled(attr.id)}
                      placeholder={formatMandatoryLabel(
                        attr,
                        t,
                        showMandatoryAsterisk
                      )}
                      options={
                        usesDependentChoices ? dependent_choices : choices
                      }
                      errors={errors}
                      formState={formState}
                      disabled={isDisabled}
                      {...creatableProps}
                      onChange={(option: CreatedOption | null) => {
                        onChange(option, { shouldValidate: true });
                        if (usesDependentChoices) {
                          updateDependentAttributes({
                            attributeId: attr.id,
                            clearSelection: option === null,
                          });
                        }
                      }}
                    />
                  )}
                />
              </FormFieldAndIcons>
            );
          }
          case "toggle":
          case "checkbox":
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                isDisabled={isDisabled}
                showApplicableButton={showApplicableButton}
                disable_applicable_button={disable_applicable_button}
              >
                <div>
                  <ToggleSwitchV2
                    label={formatMandatoryLabel(attr, t, showMandatoryAsterisk)}
                    name={attr.id}
                    ref={register({ required: attr.is_required })}
                    defaultChecked={
                      attr.values && (attr.values[0]?.value as boolean)
                    }
                    disabled={isDisabled}
                  />
                </div>
              </FormFieldAndIcons>
            );
          case "numeric": {
            const fieldKey = `${attr.id}-${
              attr.is_not_applicable ? "na" : "norm"
            }`;

            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                isDisabled={isDisabled}
                showApplicableButton={showApplicableButton}
                disable_applicable_button={disable_applicable_button}
              >
                <TextField
                  key={fieldKey}
                  name={attr.id}
                  label={`${formatMandatoryLabel(
                    attr,
                    t,
                    showMandatoryAsterisk
                  )} (number)`}
                  theref={register({
                    required: attr.is_required,
                  })}
                  defaultValue={
                    attr.values?.length
                      ? (attr.values[0].value as string)
                      : undefined
                  }
                  formState={formState}
                  errors={errors}
                  type="number"
                  readOnly={isDisabled}
                />
              </FormFieldAndIcons>
            );
          }
          case "multiline_entry":
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                isDisabled={isDisabled}
                showApplicableButton={showApplicableButton}
                disable_applicable_button={disable_applicable_button}
              >
                {attr.name === "Meta Keywords" ||
                attr.name === "Meta Description" ? (
                  <TextArea
                    name={attr.id}
                    label={formatMandatoryLabel(attr, t, showMandatoryAsterisk)}
                    theref={register({ required: attr.is_required })}
                    formState={formState}
                    required={attr.is_required}
                    defaultValue={
                      attr.values?.length
                        ? (attr.values[0].value as string)
                        : undefined
                    }
                    disabled={isDisabled}
                  />
                ) : (
                  <RichEditorLocalContainer>
                    <RichEditor
                      key={`${attr.id}-${attr.is_not_applicable}`}
                      value={
                        attr.values?.length
                          ? (attr.values[0].value as string)
                          : undefined
                      }
                      name={attr.id}
                      label={formatMandatoryLabel(
                        attr,
                        t,
                        showMandatoryAsterisk
                      )}
                      useFormMethods={{
                        register,
                        formState,
                        setValue,
                        errors,
                        watch,
                      }}
                      readOnly={isDisabled}
                    />
                  </RichEditorLocalContainer>
                )}
              </FormFieldAndIcons>
            );
          case "multi_select":
            const dependent_choices_multi_select =
              dependent_attributes_options.find(
                (constraint) => constraint.attribute_id === attr.id
              )
                ? sortOptionsByLabel(
                    dependent_attributes_options
                      .find((constraint) => constraint.attribute_id === attr.id)
                      ?.valid_values?.map((value) => ({
                        label: value.display,
                        value: value.display,
                      })) ?? []
                  )
                : undefined;
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                isDisabled={isDisabled}
                showApplicableButton={showApplicableButton}
                disable_applicable_button={disable_applicable_button}
              >
                {hasPermission("modify_lists") ? (
                  <MultiSelectField
                    key={`${attr.id}-${attr.is_not_applicable}`}
                    onCreateOption={onCreateOption}
                    attribute={attr}
                    type={"group"}
                    methodsOfUseForm={methodsOfUseForm}
                    multiSelectRefs={multiSelectRefs}
                    isDisabled={isDisabled || isAutofilled(attr.id)}
                    isNotApplicable={attr.is_not_applicable ?? false}
                    dependent_choices={dependent_choices_multi_select}
                    updateDependentAttributes={updateDependentAttributes}
                  />
                ) : (
                  <MultiSelectField
                    key={`${attr.id}-${attr.is_not_applicable}`}
                    attribute={attr}
                    type={"group"}
                    methodsOfUseForm={methodsOfUseForm}
                    multiSelectRefs={multiSelectRefs}
                    isDisabled={isDisabled || isAutofilled(attr.id)}
                    isNotApplicable={attr.is_not_applicable ?? false}
                    dependent_choices={dependent_choices_multi_select}
                    updateDependentAttributes={updateDependentAttributes}
                  />
                )}
              </FormFieldAndIcons>
            );
          case "link":
            const fieldKey = `${attr.id}-${
              attr.is_not_applicable ? "na" : "norm"
            }`;

            const maybeDefaultValue = (() => {
              if (attr?.values?.[0]?.value && !attr.is_not_applicable) {
                const parsed = LinkAttributeValueSchema.safeParse(
                  attr.values[0]?.value
                );
                if (parsed.success) {
                  return parsed.data as { url: string; display_text: string };
                }
              }
              return { url: "", display_text: "" };
            })();

            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                isDisabled={isDisabled}
                showApplicableButton={showApplicableButton}
                disable_applicable_button={disable_applicable_button}
              >
                <LinkAttributeField
                  key={fieldKey}
                  name={attr.id}
                  label={formatMandatoryLabel(attr, t, showMandatoryAsterisk)}
                  control={control}
                  register={register}
                  formState={formState}
                  errors={errors}
                  defaultValue={maybeDefaultValue}
                  isRequired={attr.is_required}
                  isDisabled={isDisabled}
                  layout="horizontal"
                />
              </FormFieldAndIcons>
            );
          case "product_reference":
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                isDisabled={isDisabled}
                showApplicableButton={showApplicableButton}
              >
                <ProductReferenceField
                  attribute={attr}
                  methodsOfUseForm={methodsOfUseForm}
                  isDisabled={isDisabled || isAutofilled(attr.id)}
                  isNotApplicable={attr.is_not_applicable ?? false}
                />
              </FormFieldAndIcons>
            );
          case "object_reference":
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                isDisabled={isDisabled}
              >
                <ObjectReferenceField
                  attribute={attr}
                  methodsOfUseForm={methodsOfUseForm}
                  isDisabled={isDisabled || isAutofilled(attr.id)}
                  isNotApplicable={attr.is_not_applicable ?? false}
                  excludeProfileId={objectSearchExcludeProfileId}
                />
              </FormFieldAndIcons>
            );
          case "user":
            const options = dependent_attributes_options.find(
              (constraint) => constraint.attribute_id === attr.id
            )
              ? dependent_attributes_options
                  .find((constraint) => constraint.attribute_id === attr.id)
                  ?.valid_values?.map((value) => ({
                    label: value.display,
                    value: value.id,
                  })) ?? []
              : undefined;
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                isDisabled={isDisabled}
                showApplicableButton={showApplicableButton}
              >
                <UserReferenceField
                  attribute={attr}
                  methodsOfUseForm={methodsOfUseForm}
                  isDisabled={isDisabled || isAutofilled(attr.id)}
                  isNotApplicable={attr.is_not_applicable ?? false}
                  dependent_attributes_options={options}
                  updateDependentAttributes={updateDependentAttributes}
                />
              </FormFieldAndIcons>
            );
          case "image":
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                isDisabled={isDisabled}
                showApplicableButton={showApplicableButton}
              >
                <ImageAttribute
                  attribute={attr}
                  methodsOfUseForm={methodsOfUseForm}
                  onCreateImageAttribute={onCreateImageAttribute}
                  isDisabled={isDisabled}
                  isNotApplicable={attr.is_not_applicable ?? false}
                />
              </FormFieldAndIcons>
            );
          case "document":
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                isDisabled={isDisabled}
                showApplicableButton={showApplicableButton}
              >
                <DocumentAttribute
                  attribute={attr}
                  methodsOfUseForm={methodsOfUseForm}
                  isDisabled={isDisabled}
                  isNotApplicable={attr.is_not_applicable ?? false}
                  product={product}
                  onCreateDocumentAttribute={onCreateDocumentAttribute}
                />
              </FormFieldAndIcons>
            );

          case "address": {
            const addressSavedValue =
              !attr.is_not_applicable && attr.values?.length
                ? (attr.values[0].value as AddressValue)
                : undefined;
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                isDisabled={isDisabled}
                showApplicableButton={showApplicableButton}
              >
                <FormProvider {...methodsOfUseForm}>
                  <AddressAttributeForm
                    key={`${attr.id}-${attr.is_not_applicable}`}
                    fieldPrefix={attr.id}
                    mode={attr.address_mode ?? "standard"}
                    attributeName={
                      attr.display_name ? attr.display_name : attr.name
                    }
                    savedValue={addressSavedValue}
                    isNotApplicable={attr.is_not_applicable ?? false}
                    disabled={isDisabled}
                  />
                </FormProvider>
              </FormFieldAndIcons>
            );
          }
          default:
            return <></>; //assertUnreachable(attr);
        }
      })}
      <XSmallButtonRow>
        <DestructiveButtonLargeV2 onClick={onCancel} type="reset">
          {t("Cancel")}
        </DestructiveButtonLargeV2>

        <PrimaryButtonLargeV2 loading={isLoading}>
          {t("Save")}
        </PrimaryButtonLargeV2>
      </XSmallButtonRow>
    </FlexForm>
  );
}

export function MultiSelectField({
  type,
  attribute,
  methodsOfUseForm,
  displayByName,
  onCreateOption,
  multiSelectRefs,
  isDisabled,
  isNotApplicable,
  dependent_choices,
  updateDependentAttributes,
  isAutofilled = false,
  menuPortalTarget,
}: {
  type: "group" | "collection";
  attribute: AttributeSchema;
  methodsOfUseForm: MethodsOfUseForm;
  displayByName?: boolean;
  onCreateOption?: AttributeGroupFormGeneratorProps["onCreateOption"];
  multiSelectRefs: React.MutableRefObject<{
    [key: string]: MultiSelectRefType;
  }>;
  isDisabled?: boolean;
  isNotApplicable: boolean;
  isAutofilled?: boolean;
  dependent_choices?: { label: string; value: string }[];
  updateDependentAttributes?: (params: {
    attributeId?: string;
    clearSelection?: boolean;
  }) => void;
  menuPortalTarget?: HTMLElement | null;
}) {
  const [attributeChips, setAttributeChips] = useState<{ name: string }[]>([]);
  const [attributeOptions, setAttributeOptions] = useState<OptionType[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { register, setValue, errors, formState } = methodsOfUseForm;
  const useLazyChoices =
    dependent_choices == null && attributeNeedsChoices(attribute);
  const {
    choices: lazyChoices,
    isLoading,
    onMenuOpen,
    onMenuScrollToBottom,
    onInputChange,
    inputValue,
    filterOption,
  } = useLazyListBackedChoices(tenant_id, attribute, useLazyChoices);

  const listChoiceOptions = useMemo(
    () =>
      dependent_choices
        ? dependent_choices
        : useLazyChoices
        ? lazyChoices.map(stringArrayToOptionArray)
        : attribute?.choices?.map(stringArrayToOptionArray) ?? [],
    [attribute?.choices, dependent_choices, lazyChoices, useLazyChoices]
  );

  const updateAttributeOptions = useCallback(
    (chips: ChipType[]) => {
      const selectednames = chips.map((chip) => chip.name);
      setAttributeOptions(
        listChoiceOptions.filter(
          (choice) => !selectednames.includes(choice.value)
        )
      );
    },
    [listChoiceOptions]
  );

  const updateFormValue = useCallback(
    (chips: ChipType[], shouldValidate: boolean = false) => {
      const val = chips.length
        ? chips.map((attribute) => attribute.name)
        : undefined;
      setValue(attribute.id, val, { shouldValidate });
    },
    [attribute.id, setValue]
  );

  const handleMultiSelectChange = (data: CreatedOption | null) => {
    if (isCreatedOption(data)) {
      const updatedAttributeChips = [
        ...attributeChips,
        { name: data.label, id: data.value },
      ];
      setAttributeChips(updatedAttributeChips);
      setIsInitialized(true);
      updateAttributeOptions(updatedAttributeChips);
      updateFormValue(updatedAttributeChips, true);
      updateDependentAttributes?.({ attributeId: attribute.id });
    }
  };

  const handleAttributeChipClick = (name: string) => {
    const updatedAttributeChips = attributeChips.filter(
      (item) => item.name !== name
    );
    setAttributeChips(updatedAttributeChips);
    setIsInitialized(true);
    updateAttributeOptions(updatedAttributeChips);
    updateFormValue(updatedAttributeChips, true);
    updateDependentAttributes?.({
      attributeId: attribute.id,
      clearSelection: updatedAttributeChips.length === 0,
    });
  };

  const creatableProps = onCreateOption
    ? buildListCreatableOptionProps({
        t,
        listName: attribute.list_name,
        optionValues: attributeOptions.map(({ value }) => value),
        onCreateOption: (value) => {
          onCreateOption(value, attribute);
          handleMultiSelectChange({ label: value, value });
        },
        enabled: !dependent_choices,
        excludeValues: attributeChips.map(({ name }) => name),
      })
    : {};

  // initialize selected chips
  useEffect(() => {
    if (!isInitialized) {
      register({
        name: attribute.id,
        required: attribute.is_required,
      });
      setIsInitialized(true);
      const getAttributeValuesChips = () => {
        const values =
          type === "group"
            ? attribute.values?.map((value) => {
                return {
                  name: value.enum_variant?.variant ?? value.attribute_id,
                };
              })
            : attribute.default_values?.map((value) => {
                return {
                  name: value.enum_variant?.variant ?? value.attribute_id,
                };
              });
        return values ?? [];
      };
      setAttributeChips(getAttributeValuesChips());
      updateFormValue(getAttributeValuesChips());
    }
  }, [
    attribute.default_values,
    attribute.id,
    attribute.is_required,
    attribute.values,
    isInitialized,
    register,
    type,
    updateFormValue,
  ]);

  useEffect(() => {
    if (isNotApplicable) {
      setAttributeChips([]);
      if (isInitialized) {
        updateFormValue([]);
      }
    }
  }, [isInitialized, isNotApplicable, updateFormValue]);

  useEffect(() => {
    updateAttributeOptions(attributeChips);
  }, [attributeChips, listChoiceOptions, updateAttributeOptions]);

  useEffect(() => {
    if (multiSelectRefs.current) {
      multiSelectRefs.current[attribute.id] = {
        clearChips: () => {
          setIsInitialized(true);
          setAttributeChips([]);
        },
        setChips: (chips: { name: string }[]) => {
          setIsInitialized(true);
          setAttributeChips(chips);
        },
      };
    }
  }, [attribute.id, multiSelectRefs]);

  return (
    <div>
      <InputWrapper>
        <SearchSelect
          name={attribute.id}
          menuPortalTarget={menuPortalTarget}
          options={
            dependent_choices
              ? dependent_choices.filter(
                  (item) =>
                    (attributeChips as { name: string }[])
                      .map((option) => option.name)
                      .indexOf(item.value) < 0
                ) ?? []
              : attributeOptions
          }
          errors={errors}
          formState={formState}
          disabled={isDisabled || !attribute.is_editable}
          isAutofilled={isAutofilled}
          isLoading={useLazyChoices ? isLoading : false}
          placeholder={formatRequiredLabel(attribute, t)}
          value={null}
          onChange={handleMultiSelectChange}
          onMenuOpen={useLazyChoices ? onMenuOpen : undefined}
          onMenuScrollToBottom={
            useLazyChoices ? onMenuScrollToBottom : undefined
          }
          onInputChange={useLazyChoices ? onInputChange : undefined}
          inputValue={useLazyChoices ? inputValue : undefined}
          filterOption={useLazyChoices ? filterOption : undefined}
          {...creatableProps}
        />
      </InputWrapper>
      <DeletableChips
        chips={
          isDisabled || !attribute.is_editable || isAutofilled
            ? [...attributeChips.map((chip) => ({ ...chip, active: false }))]
            : attributeChips
        }
        noneSelectedName={
          displayByName ? attribute.name : t([attribute.display_name])
        }
        handleClick={handleAttributeChipClick}
      />
    </div>
  );
}

export function ProductReferenceField({
  attribute,
  methodsOfUseForm,
  isDisabled,
  isNotApplicable,
}: {
  attribute: AttributeSchema;
  methodsOfUseForm: MethodsOfUseForm;
  isDisabled?: boolean;
  isNotApplicable: boolean;
  enableInput?: boolean;
}) {
  const [attributeChips, setAttributeChips] = useState<ChipType[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [chipsBeforeNotApplicable, setChipsBeforeNotApplicable] =
    useState<ChipType[] | null>(null);
  const { tenant_id } = useStoreState();
  const { t } = useTranslation();
  const { register, setValue, errors, formState, control } = methodsOfUseForm;

  const defaultValues = useMemo(() => {
    return (attribute.values ?? attribute.default_values ?? [])?.map(
      (value) => {
        return {
          name: value.product_ref?.name ?? value.attribute_id,
          id: value.product_ref?.id,
        };
      }
    );
  }, [attribute.default_values, attribute.values]);

  const updateFormValue = useCallback(
    ({
      chips,
      type,
      shouldValidate = false,
    }: {
      chips: ChipType[];
      type: "single_select" | "multi_select";
      shouldValidate?: boolean;
    }) => {
      if (type === "single_select") {
        setValue(
          attribute.id,
          chips.length
            ? { label: chips[0]?.name ?? "", value: chips[0]?.id ?? "" }
            : null,
          { shouldValidate }
        );
      } else {
        setValue(
          attribute.id,
          chips.map(({ id, name }) => ({ label: name, value: id })),
          { shouldValidate }
        );
      }
    },
    [attribute.id, setValue]
  );

  const handleMultiSelectChange = (data: CreatedOption | null) => {
    if (isCreatedOption(data)) {
      setAttributeChips((previous) => [
        ...previous,
        { name: data.label, id: data.value },
      ]);
      updateFormValue({
        chips: [...attributeChips, { name: data.label, id: data.value }],
        type: "multi_select",
        shouldValidate: true,
      });
    }
  };

  const handleSingleSelectChange = (data: CreatedOption | null) => {
    if (isCreatedOption(data)) {
      setAttributeChips([{ name: data.label, id: data.value }]);
      updateFormValue({
        chips: [{ name: data.label, id: data.value }],
        type: "single_select",
        shouldValidate: true,
      });
    } else if (data === null) {
      setAttributeChips([]);
      updateFormValue({
        chips: [],
        type: "single_select",
        shouldValidate: true,
      });
    }
  };

  useEffect(() => {
    if (!isInitialized) {
      // Initialize chips from values when applicable
      register({
        name: attribute.id,
        required: attribute.is_required,
      });
      setIsInitialized(true);
      setAttributeChips(defaultValues);
      updateFormValue({
        chips: defaultValues,
        type: attribute.is_multiple_choice ? "multi_select" : "single_select",
      });
    }
  }, [
    attribute.id,
    attribute.is_multiple_choice,
    attribute.is_required,
    defaultValues,
    isInitialized,
    register,
    updateFormValue,
  ]);

  const handleAttributeChipClick = (name: string) => {
    const newAttributeChips = attributeChips.filter(
      (item) => item.name !== name
    );
    setAttributeChips(newAttributeChips);
    updateFormValue({
      chips: newAttributeChips,
      type: attribute.is_multiple_choice ? "multi_select" : "single_select",
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (isNotApplicable) {
      if (chipsBeforeNotApplicable === null) {
        setChipsBeforeNotApplicable(attributeChips);
      }

      if (attributeChips.length > 0) {
        setAttributeChips([]);
        updateFormValue({
          chips: [],
          type: attribute.is_multiple_choice ? "multi_select" : "single_select",
        });
      }
      return;
    }

    if (!isNotApplicable && chipsBeforeNotApplicable !== null) {
      const chipsToRestore =
        chipsBeforeNotApplicable && chipsBeforeNotApplicable.length > 0
          ? chipsBeforeNotApplicable
          : defaultValues;
      setAttributeChips(chipsToRestore);
      updateFormValue({
        chips: chipsToRestore,
        type: attribute.is_multiple_choice ? "multi_select" : "single_select",
      });
      setChipsBeforeNotApplicable(null);
    }
  }, [
    attribute.is_multiple_choice,
    attributeChips,
    chipsBeforeNotApplicable,
    defaultValues,
    isInitialized,
    isNotApplicable,
    updateFormValue,
  ]);

  return (
    <div>
      <InputWrapper>
        <Controller
          name={attribute.id}
          control={control}
          key={attribute.id}
          defaultValue={
            defaultValues.length
              ? attribute.is_multiple_choice
                ? defaultValues.map((value) => ({
                    label: value.name,
                    value: value.id,
                  }))
                : { label: defaultValues[0].name, value: defaultValues[0].id }
              : null
          }
          render={({ onChange, ...rest }) => (
            <SearchSelectInfiniteScroll
              {...rest}
              name={attribute.id}
              errors={errors}
              formState={formState}
              isClearable
              disabled={isDisabled || !attribute.is_editable}
              placeholder={formatRequiredLabel(attribute, t)}
              baseUrl={endpoints.v2_tenants_id_pim_products_summary(tenant_id)}
              params={(() => {
                const params = new URLSearchParams();
                params.append("order_by", "asc");
                params.append("status", "published");
                return params;
              })()}
              getOptions={(response: PIMProduct[]) =>
                response
                  .reduce(
                    (
                      prev: { label: string; value: string }[],
                      { name, id }: PIMProduct
                    ) => [...prev, { label: name, value: id }],
                    []
                  )
                  .filter(
                    (item) =>
                      attributeChips
                        .map((option) => option.name)
                        .indexOf(item.label) < 0
                  )
              }
              onChange={
                attribute.is_multiple_choice
                  ? handleMultiSelectChange
                  : handleSingleSelectChange
              }
              testid={attribute.name}
              autoComplete="on"
              {...(attribute.is_multiple_choice
                ? { value: null }
                : { value: rest.value })}
            />
          )}
        />
      </InputWrapper>
      {attribute.is_multiple_choice && (
        <DeletableChips
          chips={
            isDisabled || !attribute.is_editable
              ? [...attributeChips.map((chip) => ({ ...chip, active: false }))]
              : attributeChips
          }
          noneSelectedName="choices"
          handleClick={handleAttributeChipClick}
        />
      )}
    </div>
  );
}

export function UserReferenceField({
  attribute,
  methodsOfUseForm,
  isDisabled,
  isNotApplicable,
  isTemplate,
  dependent_attributes_options,
  updateDependentAttributes,
  normalPlaceholder = false,
  menuPortalTarget,
}: {
  attribute: AttributeSchema;
  methodsOfUseForm: MethodsOfUseForm;
  isDisabled?: boolean;
  isNotApplicable: boolean;
  isTemplate?: boolean;
  dependent_attributes_options?: { label: string; value: string }[];
  updateDependentAttributes?: (params: {
    attributeId?: string;
    clearSelection?: boolean;
  }) => void;
  normalPlaceholder?: boolean;
  menuPortalTarget?: HTMLElement | null;
}) {
  const [attributeChips, setAttributeChips] = useState<ChipType[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [chipsBeforeNotApplicable, setChipsBeforeNotApplicable] =
    useState<ChipType[] | null>(null);
  const { tenant_id, storefront_id } = useStoreState();
  const userByIdRef = useRef<Record<string, User>>({});
  const { t } = useTranslation();
  const { register, setValue, errors, formState, control } = methodsOfUseForm;

  const defaultValues = useMemo(() => {
    const isFromDefaultValues =
      !!attribute.default_values && attribute.default_values.length > 0;
    const attributeData = isFromDefaultValues
      ? attribute.default_values
      : attribute.values;

    const values = attributeData?.map((value) => {
      const userData = isFromDefaultValues
        ? (value.value as User)
        : (value.user as User);
      const userOption = convertUserToOption(userData, false);
      return {
        name: userOption ? userOption.label : value.attribute_id,
        id: userOption.value,
      };
    });

    return values ?? [];
  }, [attribute.default_values, attribute.values]);

  const updateFormValue = useCallback(
    ({
      chips,
      type,
      shouldValidate = false,
    }: {
      chips: ChipType[];
      type: "single_select" | "multi_select";
      shouldValidate?: boolean;
    }) => {
      if (type === "single_select") {
        setValue(
          attribute.id,
          chips.length
            ? { label: chips[0]?.name ?? "", value: chips[0]?.id ?? "" }
            : null,
          { shouldValidate: !isTemplate && shouldValidate }
        );
      } else {
        setValue(
          attribute.id,
          chips.map(({ id, name }) => ({ label: name, value: id })),
          { shouldValidate: !isTemplate && shouldValidate }
        );
      }
    },
    [attribute.id, isTemplate, setValue]
  );

  const handleMultiSelectChange = (data: CreatedOption | null) => {
    if (isCreatedOption(data)) {
      const displayName = data.label.split(" - ")[0].trim();
      const newAttributeChips = [
        ...attributeChips,
        { name: displayName, id: data.value },
      ];
      setAttributeChips(newAttributeChips);
      updateFormValue({
        chips: newAttributeChips,
        type: "multi_select",
        shouldValidate: true,
      });
    }
    if (dependent_attributes_options && updateDependentAttributes) {
      setTimeout(() => {
        updateDependentAttributes({ attributeId: attribute.id });
      }, 200);
    }
  };

  const handleSingleSelectChange = (data: CreatedOption | null) => {
    if (isCreatedOption(data)) {
      const displayName = data.label.split(" - ")[0].trim();
      const newAttributeChips = [{ name: displayName, id: data.value }];
      setAttributeChips(newAttributeChips);
      updateFormValue({
        chips: newAttributeChips,
        type: "single_select",
        shouldValidate: true,
      });
    } else {
      updateFormValue({
        chips: [],
        type: "single_select",
        shouldValidate: true,
      });
    }
    if (dependent_attributes_options && updateDependentAttributes) {
      setTimeout(() => {
        updateDependentAttributes({ attributeId: attribute.id });
      }, 200);
    }
  };

  useEffect(() => {
    if (!isInitialized) {
      register({
        name: attribute.id,
        required: isTemplate ? false : attribute.is_required,
      });
      setIsInitialized(true);
      setAttributeChips(defaultValues);
      updateFormValue({
        chips: defaultValues,
        type: attribute.is_multiple_choice ? "multi_select" : "single_select",
      });
    }
  }, [
    attribute.id,
    attribute.is_multiple_choice,
    attribute.is_required,
    defaultValues,
    isInitialized,
    isTemplate,
    register,
    updateFormValue,
  ]);

  const handleAttributeChipClick = (name: string) => {
    const newAttributeChips = attributeChips.filter(
      (item) => item.name !== name
    );
    setAttributeChips(newAttributeChips);
    updateFormValue({
      chips: newAttributeChips,
      type: "multi_select",
      shouldValidate: true,
    });
    if (dependent_attributes_options && updateDependentAttributes) {
      setTimeout(() => {
        updateDependentAttributes({ attributeId: attribute.id });
      }, 200);
    }
  };

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (isNotApplicable) {
      if (chipsBeforeNotApplicable === null) {
        setChipsBeforeNotApplicable(attributeChips);
      }

      if (attributeChips.length > 0) {
        setAttributeChips([]);
        updateFormValue({
          chips: [],
          type: attribute.is_multiple_choice ? "multi_select" : "single_select",
        });
      }
      return;
    }

    if (!isNotApplicable && chipsBeforeNotApplicable !== null) {
      const chipsToRestore =
        chipsBeforeNotApplicable && chipsBeforeNotApplicable.length > 0
          ? chipsBeforeNotApplicable
          : defaultValues;
      setAttributeChips(chipsToRestore);
      updateFormValue({
        chips: chipsToRestore,
        type: attribute.is_multiple_choice ? "multi_select" : "single_select",
      });
      setChipsBeforeNotApplicable(null);
    }
  }, [
    attribute.is_multiple_choice,
    attributeChips,
    chipsBeforeNotApplicable,
    defaultValues,
    isInitialized,
    isNotApplicable,
    updateFormValue,
  ]);

  const getUserOptions = (response: User[]) =>
    response.reduce(
      (prev: { label: string; value: string }[], user: User) => [
        ...prev,
        convertUserToOption(user, false),
      ],
      []
    );

  return (
    <div>
      <InputWrapper>
        <Controller
          name={attribute.id}
          control={control}
          key={attribute.id}
          rules={{
            required: !isTemplate && attribute.is_required,
          }}
          defaultValue={
            defaultValues.length
              ? attribute.is_multiple_choice
                ? defaultValues.map((value) => ({
                    label: value.name,
                    value: value.id,
                  }))
                : { label: defaultValues[0].name, value: defaultValues[0].id }
              : null
          }
          render={({ onChange, ...rest }) => (
            <SearchSelectInfiniteScroll
              {...rest}
              errors={errors}
              formState={formState}
              isClearable
              disabled={isDisabled || !attribute.is_editable}
              normalPlaceholder={normalPlaceholder}
              placeholder={formatRequiredLabel(attribute, t)}
              baseUrl={
                dependent_attributes_options
                  ? null
                  : endpoints.v1_storefronts_id_tenants_id_users(
                      storefront_id,
                      tenant_id
                    )
              }
              params={
                dependent_attributes_options
                  ? undefined
                  : (() => {
                      const params = new URLSearchParams();
                      params.append("order_by", "asc");
                      params.append("is_active", "true");
                      return params;
                    })()
              }
              options={dependent_attributes_options?.filter(
                (item) =>
                  attributeChips
                    .map((option) => option.name)
                    .indexOf(item.label) < 0
              )}
              getOptions={
                dependent_attributes_options
                  ? () => []
                  : (response: User[]) => {
                      response.forEach((u) => {
                        userByIdRef.current[String(u.id)] = u;
                      });
                      return getUserOptions(response).filter(
                        (item) =>
                          attributeChips
                            .map((option) => option.name)
                            .indexOf(item.label.split(" - ")[0].trim()) < 0
                      );
                    }
              }
              onChange={
                attribute.is_multiple_choice
                  ? handleMultiSelectChange
                  : handleSingleSelectChange
              }
              testid={attribute.name}
              autoComplete="on"
              {...(attribute.is_multiple_choice
                ? { value: null }
                : { value: rest.value })}
              menuPortalTarget={menuPortalTarget}
            />
          )}
        />
      </InputWrapper>
      {attribute.is_multiple_choice ? (
        <DeletableChips
          chips={
            isDisabled || !attribute.is_editable
              ? [...attributeChips.map((chip) => ({ ...chip, active: false }))]
              : attributeChips
          }
          noneSelectedName="choices"
          handleClick={handleAttributeChipClick}
        />
      ) : (
        <></>
      )}
    </div>
  );
}
